import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/link";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { getAuditColumns } from "@/pages/a7/Audit/getAuditColumns";

import { CheckIcon } from "@radix-ui/react-icons";

export type A7FIAInstrumentTS = {
  id: number;
  date: Date;
  a7_fia_instrument_id: number;
  subordination: string;
  restructuration: string;
  a7_fia_issuer_id?: number;
  a7_fia_issuer_name?: string;
};

const tableName = "a7_fia_instrument_ts";

export const getInstrumentsTsColumns = (
  t: TFunction,
): ColumnDef<A7FIAInstrumentTS>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "ID"),
    },
    {
      accessorKey: "date",
      header: tTable("date", "Date"),
    },
    {
      accessorKey: "a7_fia_instrument_id",
      header: tTable("a7_fia_instrument_id", "Financial instrument"),
    },
    {
      accessorKey: "a7_fia_issuer_name",
      header: tTable("a7_fia_issuer_name", "Issuer"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-fia/a7_fia_issuers/${row.original.a7_fia_issuer_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_fia_issuer_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "subordination",
      header: () => (
        <div className="text-center">
          {tTable("subordination", "Subordination")}
        </div>
      ),
      cell: ({ getValue }) =>
        getValue() == "YES" && (
          <div className="flex justify-center">
            <CheckIcon />
          </div>
        ),
    },
    {
      accessorKey: "restructuration",
      header: () => (
        <div className="text-center">
          {tTable("restructuration", "Restructuring")}
        </div>
      ),
      cell: ({ getValue }) =>
        getValue() == "YES" && (
          <div className="flex justify-center">
            <CheckIcon />
          </div>
        ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getInstrumentTsAuditColumns = (t: TFunction) =>
  getAuditColumns<A7FIAInstrumentTS>(getInstrumentsTsColumns(t)).filter(
    (column) => {
      const accessorKey =
        "accessorKey" in column
          ? String((column as { accessorKey?: unknown }).accessorKey ?? "")
          : "";
      return !["a7_fia_issuer_name", "id"].includes(accessorKey);
    },
  );
